import { Box, Tooltip } from '@deltasierra/react-components-core';
import { theme } from '@deltasierra/react-theme';
import { Translations } from '@deltasierra/translations-react';
import { type ComponentProps, type MouseEvent } from 'react';
import { AssetLibraryGalleryGridAssetButtons } from './AssetLibraryGalleryGridAssetButtons';
import { AssetLibraryGalleryGridItemFolder } from './AssetLibraryGalleryGridItemFolder';
import { AssetLibraryGalleryGridItemThumbnail } from './AssetLibraryGalleryGridItemThumbnail';
import { AssetType } from './constants';
import { Asset, AssetWithDimensions } from './types';

export type AssetLibraryGalleryGridItemBaseProps = {
    asset: AssetWithDimensions;
    isSelected: boolean;
    itemActions: Partial<
            Pick<
                ComponentProps<typeof Box>,
                'onClick' | 'onDoubleClick' | 'onPointerCancel' | 'onPointerDown' | 'onPointerLeave' | 'onPointerUp'
            >
        >;
    onClickAssetPreview?: (asset: Asset) => void;
    onClickDownload?: (value?: { assetId: string }) => void;
    onClickOpenActions?: (event: MouseEvent<HTMLElement>, asset: Asset) => void;
    onClickSelectItem?: (asset: Asset) => void;
    t: Translations<'AssetLibrary'>;
};

export const AssetLibraryGalleryGridItemBase = ({
    asset,
    isSelected,
    itemActions,
    onClickAssetPreview,
    onClickDownload,
    onClickOpenActions,
    onClickSelectItem,
    t,
}: AssetLibraryGalleryGridItemBaseProps): JSX.Element => {
    const { height, width } = asset.dimensions;

    const isWithActions = !!onClickAssetPreview || !!onClickDownload || !!onClickOpenActions || !!onClickSelectItem;

    return (
        <Tooltip title={asset.title} disableInteractive>
            <Box
                data-asset-name={asset.name}
                data-asset-type={asset.type}
                data-cy="asset-library-gallery-grid-item"
                sx={{
                    '&:hover': {
                        '.asset-library-gallery-grid-asset-buttons': {
                            opacity: 1,
                        },
                        outline: `2px solid ${theme.palette.primary.main}`,
                    },
                    borderRadius: 1,
                    cursor: 'pointer',
                    height,
                    outline: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                    position: 'relative',
                    transition: 'outline-color 0.2s ease-in-out',
                    width,
                }}
                {...itemActions}
            >
                {asset.type === AssetType.Asset ? (
                    <AssetLibraryGalleryGridItemThumbnail
                        alt={asset.name}
                        height={height}
                        mimeType={asset.mimeType}
                        url={asset.thumbnailUrl}
                        width={width}
                    />
                ) : (
                    <AssetLibraryGalleryGridItemFolder folderName={asset.name} height={height} width={width} />
                )}
                {isWithActions && (
                    <AssetLibraryGalleryGridAssetButtons
                        assetType={asset.type}
                        isSelected={isSelected}
                        permissions={asset.permissions}
                        t={t}
                        onClickAssetPreview={() => onClickAssetPreview?.(asset)}
                        onClickDownload={() => onClickDownload?.({ assetId: asset.id })}
                        onClickOpenActions={event => onClickOpenActions?.(event, asset)}
                        onClickSelectItem={() => onClickSelectItem?.(asset)}
                    />
                )}
            </Box>
        </Tooltip>
    );
};

AssetLibraryGalleryGridItemBase.displayName = 'AssetLibraryGalleryGridItemBase';
